import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormValidation } from '../../shared/forms/form-validation';
import { FormUtility } from '../../shared/forms/form-utility';
import { DialogComponent } from '../../shared/dialog/dialog.component';
import { FileUploadComponent } from '../../shared/file-upload/file-upload.component';
import { FileTypeConstants } from '../../shared/constants/file-upload-type-constants';
import { LoadingService } from '../../shared/loading.service';
import { ClaimService } from '../claims/claim.service';
import { Subscription } from 'rxjs';
import { DocumentUploadMapper } from './document-upload-mapper';
import { DocumentUploadRequest } from './document-upload-request';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { environment } from '../../environments/environment';

@Component({
  selector: 'claims-document-upload',
  standalone: false,
  templateUrl: './claims-document-upload.component.html',
})
export class ClaimsDocumentUploadComponent implements OnInit {
  @ViewChild(FileUploadComponent) fileUpload: FileUploadComponent;

  public baseClaimSubmissionWebUrl = `${environment.baseClaimSubmissionWebUrl}/claims/document-upload`;
  public uploadDocumentForm: UntypedFormGroup;
  public formCompleted = false;
  public claimServiceSubscription: Subscription;
  public mbiWarningDialog: MatDialogRef<DialogComponent>;
  public submitClicked = false;
  public documentsUploaded = false;
  public readonly customer = 'CUSTOMER';
  public readonly repairFacility = 'REPAIR FACILITY';
  public missingDocuments: boolean = false;
  public userTypeValid: boolean = true;
  public readonly minCurrentMileage = 0;
  public readonly maxCurrentMileage = 9999999;
  public faCheck = faCheck;
  private recaptchaAction = 'document_upload_submission';
  public maxFileSizeMB = environment.maxFileSizeMB as unknown as number;
  public maxFiles = environment.maxFiles as unknown as number;
  public acceptedFiles: string[] = [FileTypeConstants.txt,
  FileTypeConstants.doc,
  FileTypeConstants.docx,
  FileTypeConstants.pdf,
  FileTypeConstants.jpeg,
  FileTypeConstants.jpg,
  FileTypeConstants.png,
  FileTypeConstants.PNG,
  FileTypeConstants.gif];

  constructor(
    private recaptchaV3Service: ReCaptchaV3Service,
    private claimService: ClaimService,
    private dialog: MatDialog,
    private loadingService: LoadingService) { }

  ngOnInit(): void {
    this.uploadDocumentForm = new UntypedFormGroup({
      'userType': new UntypedFormControl("", [Validators.required]),
      'contractNumber': new UntypedFormControl("", [Validators.required, Validators.pattern(/^[A-Za-z0-9]{7,20}$/)]),
      'claimNumber': new UntypedFormControl(""),
      'customerFirstName': new UntypedFormControl("", [Validators.required]),
      'customerLastName': new UntypedFormControl("", [Validators.required]),
      'emailAddress': new UntypedFormControl("", [Validators.required, Validators.email]),
      'phoneNumber': new UntypedFormControl("", [Validators.required]),
      'message': new UntypedFormControl("")
    });
  }

  ngOnDestroy(): void {
    if (this.claimServiceSubscription) {
      this.claimServiceSubscription.unsubscribe();
    }
  }

  public get readyToSubmit(): boolean {
    return this.uploadDocumentForm.dirty;
  }

  //** Validates the customer form and sets formCompleted to valid or invalid */
  public validateForm(): void {
    this.fileUpload?.validateForm();
    this.formCompleted = FormValidation.isValid(this.uploadDocumentForm) && this.fileUpload?.formCompleted;
    this.missingDocuments = !this.fileUpload?.formCompleted;
    this.userTypeValid = this.uploadDocumentForm.controls['userType'].valid;
  }

  /**
   * Resets the form.
   * @see {@link FormUtility.reset}
   */
  public reset(): void {
    FormUtility.reset(this.uploadDocumentForm);
    this.fileUpload.clearFiles();
    this.formCompleted = false;
    this.missingDocuments = false;
    this.userTypeValid = true;
    this.submitClicked = false;
  }

  /**
   * Clears the value in the specified form field.
   * @param field The form field to clear.
   */
  public clear(field: string): void {
    FormUtility.clear(this.uploadDocumentForm, field);
  }

  /**
   * Gets a value indicating if the specified field has a value.
   * @param field The form field to check.
   * @returns If the field has a value, `true`; otherwise, `false`.
   */
  public hasValue(field: string): boolean {
    return FormUtility.hasValue(this.uploadDocumentForm, field)
  }

  public submit(): void {
    this.validateForm();
    if (this.uploadDocumentForm.valid && this.fileUpload.formCompleted) {
      this.loadingService.show();
      this.submitClicked = true;
      const documents: File[] = this.fileUpload.importFiles ?? [];
      const request = DocumentUploadMapper.mapClaimRequest(this.uploadDocumentForm);
      this.recaptchaV3Service.execute(this.recaptchaAction)
        .subscribe({
          next: (token: string) => {
            this.claimService.reCaptchaToken = token;
            this.saveClaimDocuments(documents, request);
          },
          error: error => {
            this.loadingService.hide();
            alert('There was an error submitting the form, please and try again later.');
            this.submitClicked = false;
            throw (error);
          }
        });
    }
    else {
      // alert('The form is invalid, please provide the required information and try again');
    }
  }

  private saveClaimDocuments(documents: File[], request: DocumentUploadRequest) {
    this.claimServiceSubscription = this.claimService.uploadClaimDocuments(documents, request)
      .subscribe({
        next: _ => {
          this.loadingService.hide();
          this.documentsUploaded = true;
        },
        error: error => {
          this.loadingService.hide();
          alert(error.error?.detail || 'There was an error submitting the form, please try again later.');
          this.submitClicked = false;
          throw (error);
        }
      });
  }
}